import { Injectable } from "@snap/ts-inject";
import { RemoteApiInfo } from "../../generated-proto/pb_schema/camera_kit/v3/features/remote_api_info";
import {
    ResponseCode,
    responseCodeToNumber,
} from "../../generated-proto/pb_schema/lenses/remote_api/remote_api_service";
import type { LensRepository } from "../../lens/LensRepository";
import type { Lens } from "../../lens/Lens";
import type { LensState } from "../../session/lensState";
import type { SessionState } from "../../session/sessionState";
import { getLogger } from "../../logger/logger";
import type { EnumToPublicStringLiteralMap, ExcludeKeys } from "../../common/types";
import type { MetricsClient } from "../../clients/metricsClient";
import { Count } from "../../metrics/operational/Count";
import { joinMetricNames } from "../../metrics/operational/Metric";
import { createUriRequestProcessor } from "../uriRequestProcessor";
import type { UriHandler, UriResponse } from "../UriHandlers";
import { extractSchemeAndRoute } from "../UriHandlers";

// NOTE: There's potential for overloads when reporting metrics if reporting is triggered on each frame,
// (i.e., when the lens sends Remote API requests every frame).
// As of now, this isn't a concern because src/metrics/operational/operationalMetricsReporter.ts aggregates
// "count" metrics into a single metric within a batch, and the Remote API service currently
// reports only "count" metrics. For instance, if 30 metrics with the same name are generated per second,
// given the current bundle size of 100 operational metrics, there will be one metrics report approximately
// every 3.3 seconds.
// In the future, if we opt to report "histogram" or other metric types, they must be approached with caution:
// either the operationalMetricsReporter should be enhanced to aggregate such metrics,
// or the Remote API service should manage it directly.
// Mobiles ticket: https://jira.sc-corp.net/browse/CAMKIT-3092

const logger = getLogger("RemoteApiServices");

const uriResponseOkCode = 200;
const apiResponseStatusHeader = ":sc_lens_api_status";
const apiBinaryContentType = "application/octet-stream";
const remoteApiInfoProtobufTypeUrl = "type.googleapis.com/com.snap.camerakit.v3.features.RemoteApiInfo";
const remoteApiUploadImageSpecId = "af3f69c8-2e62-441f-8b1c-d3956f7b336c";

const statusToResponseCodeMap: ExcludeKeys<
    EnumToPublicStringLiteralMap<typeof ResponseCode>,
    "responseCodeUnset" | "unrecognized"
> = {
    success: ResponseCode.SUCCESS,
    redirected: ResponseCode.REDIRECTED,
    badRequest: ResponseCode.BAD_REQUEST,
    accessDenied: ResponseCode.ACCESS_DENIED,
    notFound: ResponseCode.NOT_FOUND,
    timeout: ResponseCode.TIMEOUT,
    requestTooLarge: ResponseCode.REQUEST_TOO_LARGE,
    serverError: ResponseCode.SERVER_ERROR,
    cancelled: ResponseCode.CANCELLED,
    proxyError: ResponseCode.PROXY_ERROR,
    // NOTE: This rule helps keep the public-facing RemoteApiStatus type consistent with the proto.
    // We prefer using a separate type for TypeDoc purposes.
} satisfies Record<RemoteApiStatus, ResponseCode>;

/**
 * Status of a Remote API response.
 */
export type RemoteApiStatus =
    | "success"
    | "redirected"
    | "badRequest"
    | "accessDenied"
    | "notFound"
    | "timeout"
    | "requestTooLarge"
    | "serverError"
    | "cancelled"
    | "proxyError";

/**
 * Remote API request sent by a lens.
 */
export interface RemoteApiRequest {
    /**
     * Unique id of the remote API service specification.
     */
    apiSpecId: string;
    /**
     * Unique id of the remote API service endpoint requested by this request.
     */
    endpointId: string;
    /**
     * A map of named parameters associated with the request.
     */
    parameters: Record<string, string>;
    /**
     * Additional binary request payload.
     */
    body: ArrayBuffer;
}

/**
 * Remote API response to a request sent by a lens.
 */
export interface RemoteApiResponse {
    /**
     * Status of the response
     */
    status: RemoteApiStatus;
    /**
     * A map of named metadata associated with the response.
     */
    metadata: Record<string, string>;
    /**
     * Additional binary request payload.
     */
    body: ArrayBuffer;
}

/**
 * Represents a Remote API request cancellation handler function.
 */
export type RemoteApiCancelRequestHandler = () => void;

/**
 * Represents a Remote API request handler function.
 * It is provided with a reply callback that must be invoked to send a response back to the lens.
 * The reply callback can be invoked multiple times if needed.
 * Additionally, the handler can return a cancellation callback, which is triggered when the lens cancels the request.
 */
export type RemoteApiRequestHandler = (
    reply: (response: RemoteApiResponse) => void
) => RemoteApiCancelRequestHandler | void;

/**
 * Service to handle a lens Remote API request.
 */
export interface RemoteApiService {
    /**
     * Remote API spec ID(s).
     */
    apiSpecId: string;

    /**
     * This method is called by Camera Kit when a lens triggers a Remote API request with a corresponding spec ID.
     * If the service can handle the request, the method returns a request handler; otherwise, it returns nothing.
     * @param request Remote API request object.
     * @param lens Lens that triggers the request.
     * @returns A request handler if applicable.
     */
    getRequestHandler(request: RemoteApiRequest, lens: Lens): RemoteApiRequestHandler | undefined;
}

export type RemoteApiServices = RemoteApiService[];

export const remoteApiServicesFactory = Injectable("remoteApiServices", () => {
    const remoteApiServices: RemoteApiServices = [];
    return remoteApiServices;
});

/**
 * Goes over provided services and returns the first request handler that is able to handle the request.
 */
function getFirstRequestHandler(services: RemoteApiService[], remoteApiRequest: RemoteApiRequest, lens: Lens) {
    for (const service of services) {
        try {
            return service.getRequestHandler(remoteApiRequest, lens);
        } catch (error) {
            logger.warn("Client's Remote API request handler factory threw an error.", error);
        }
    }
    return undefined;
}

/**
 * Provides a URI handler that searches for a match within the provided services to handle Remote API requests,
 * i.e., those whose URI starts with 'app://remote-api/performApiRequest'.
 */
export function createRemoteApiUriHandler(
    registeredServices: RemoteApiService[],
    sessionState: SessionState,
    lensState: LensState,
    lensRepository: LensRepository,
    metrics: MetricsClient
): UriHandler {
    // Groups services by spec ID for faster lookups.
    const registeredServiceMap = new Map<string, RemoteApiService[]>();
    for (const service of registeredServices) {
        const existingServices = registeredServiceMap.get(service.apiSpecId) || [];
        registeredServiceMap.set(service.apiSpecId, [...existingServices, service]);
    }

    return createUriRequestProcessor({
        uri: "app://remote-api/performApiRequest",
        lensState,
        sessionState,
        createLensRequestState(_request, lens) {
            return {
                supportedSpecIds: new Set([
                    // Parse lens metadata to obtain supported Remote API specs.
                    ...(lensRepository.getLensMetadata(lens.id)?.featureMetadata ?? [])
                        .filter((feature) => feature.typeUrl === remoteApiInfoProtobufTypeUrl)
                        .flatMap((any) => RemoteApiInfo.decode(any.value).apiSpecIds),
                    // Also add remoteApiUploadImageSpecId as this spec should be available for all lenses.
                    // See https://jira.sc-corp.net/browse/CAMKIT-6245.
                    remoteApiUploadImageSpecId,
                ]),
            };
        },
        processRequest({ request, reply, lens, setCancellationHandler, state }) {
            // Extracts the spec ID and endpoint ID from the provided Remote API request URI.
            // The given URI is expected to conform to the following specification:
            // eslint-disable-next-line max-len
            // https://docs.google.com/document/d/18fbGYDhD2N_aMTe4ZLY4QKeCSoMeJuklG28TutDzLZc/edit#bookmark=id.p2y39gwgbm4g
            const { route } = extractSchemeAndRoute(request.uri);
            const [specId, endpointIdWithQuery] = route.split("/").slice(2);
            const [endpointId] = endpointIdWithQuery.split("?");

            const dimensions = { specId };
            const reportSingleCount = (name: string) => {
                metrics.setOperationalMetrics(
                    Count.count(joinMetricNames(["lens", "remote-api", name]), 1, dimensions)
                );
            };
            reportSingleCount("requests");

            // Only handle requests for API spec ID that current lens supports.
            if (!state?.supportedSpecIds.has(specId)) return;

            const remoteApiRequest: RemoteApiRequest = {
                apiSpecId: specId,
                body: request.data,
                endpointId,
                parameters: request.metadata,
            };

            let requestHandler = getFirstRequestHandler(registeredServiceMap.get(specId) ?? [], remoteApiRequest, lens);
            if (!requestHandler) return;

            reportSingleCount("handled-requests");

            let cancellationHandler: RemoteApiCancelRequestHandler | void = undefined;
            try {
                // Calls client's Remote API handler to process the request.
                cancellationHandler = requestHandler((response) => {
                    reportSingleCount("responses");

                    const responseCode = statusToResponseCodeMap[response.status] ?? ResponseCode.UNRECOGNIZED;
                    const uriResponse: UriResponse = {
                        code: uriResponseOkCode,
                        description: "",
                        contentType: apiBinaryContentType,
                        data: response.body,
                        metadata: {
                            ...response.metadata,
                            [apiResponseStatusHeader]: responseCodeToNumber(responseCode).toString(),
                        },
                    };
                    reply(uriResponse);
                });
            } catch (error) {
                logger.warn("Client's Remote API request handler threw an error.", error);
            }

            if (typeof cancellationHandler === "function") {
                setCancellationHandler(() => {
                    try {
                        cancellationHandler!();
                    } catch (error) {
                        logger.warn("Client's Remote API request cancellation handler threw an error.", error);
                    }
                });
            }
        },
        processInternalError(error) {
            // The expectation is that if an error occurs, it happens in our own implementation,
            // because app callbacks are wrapped with try..catch blocks.
            // Therefore, we would like to report this error.
            logger.error(error);
            metrics.setOperationalMetrics(Count.count(joinMetricNames(["lens", "remote-api", "errors"]), 1));
        },
    });
}
